import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { Play, ExternalLink, Eye, EyeOff, Copy, Check, Star, Send, Maximize2, X, Smartphone, Monitor, ShoppingCart, MessageSquare, ShieldCheck, UserCheck, Layers } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import { toast } from 'sonner';
import { formatMoneyCompact } from '../../lib/format';
import ReviewsModal from './ReviewsModal';
import { normalizeUrl } from '../../lib/utils';

export default function Products({ onEnquire, onBuy }) {
  const { data, walletUserEnabled } = useSite();
  const { matches, query, category } = useFilter();
  const products = data.products || [];
  const [tab, setTab] = useState(products[0]?.id || 'b2c');
  const buyEnabled = walletUserEnabled !== false;

  // Map a nav/sidebar category (e.g. "b2cwl" → "B2C White Label" tab) to a product tab.
  const tabForCategory = (cat) => {
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const c = norm(cat);
    if (!c || c === 'home') return null;
    const aliases = new Set([c]);
    const stripped = c.replace(/(wl|ui|id|api)$/, '');
    if (stripped) aliases.add(stripped);
    return products.find((p) => {
      const t = norm(`${p.id} ${p.label}`);
      return [...aliases].some((a) => a && t.includes(a));
    }) || null;
  };

  // Nav/sidebar category click → switch to matching tab
  React.useEffect(() => {
    const hit = tabForCategory(category);
    if (!hit) return;
    if (hit.id !== tab) setTab(hit.id);
    // eslint-disable-next-line
  }, [category]);

  // Free-text search → jump to matching tab
  React.useEffect(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return;
    const hit = products.find((p) => {
      const catText = (`${p.id} ${p.label}`).toLowerCase();
      const hasItem = (p.items || []).some((it) => matches(it));
      return catText.includes(q) || hasItem;
    });
    if (hit && hit.id !== tab) setTab(hit.id);
    // eslint-disable-next-line
  }, [query]);

  const active = products.find((p) => p.id === tab) || products[0];
  if (data.productsEnabled === false) return null;
  if (!active) return null;

  const filteredItems = (active.items || []).filter(matches);

  const isB2C = active.id.toLowerCase().includes('b2c');
  const isB2B = active.id.toLowerCase().includes('b2b');

  return (
    <div id="products" className="rounded-2xl bg-zinc-950/70 border border-zinc-800/80 shadow-2xl p-4 md:p-6 my-4">
      {/* Header bar with Category tabs */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mb-5 pb-4 border-b border-zinc-800">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-[color:var(--gold)] text-black flex items-center justify-center font-extrabold shadow-sm shrink-0">
            <Layers size={15} />
          </span>
          <div>
            <h2 className="text-white font-extrabold text-base md:text-lg tracking-tight">
              MARKETPLACE PRODUCTS
            </h2>
            <p className="text-zinc-400 text-xs mt-0.5 hidden sm:block">
              {active.description || 'Explore turnkey white labels, lines, and developer suites'}
            </p>
          </div>
        </div>

        {/* Symmetrical Category Pills */}
        <div className="flex items-center gap-1.5 p-1 bg-zinc-900/90 border border-zinc-800 rounded-xl overflow-x-auto no-scrollbar max-w-full">
          {products.map((p) => {
            const pIsB2C = p.id.toLowerCase().includes('b2c');
            const pIsB2B = p.id.toLowerCase().includes('b2b');
            const isCurrent = tab === p.id;
            return (
              <button
                type="button"
                key={p.id}
                id={`category-${p.id}`}
                data-anchor={`category-${p.id}`}
                onClick={() => setTab(p.id)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all scroll-mt-32 ${
                  isCurrent
                    ? pIsB2C
                      ? 'bg-emerald-500 text-black shadow-md font-extrabold'
                      : pIsB2B
                      ? 'bg-cyan-400 text-black shadow-md font-extrabold'
                      : 'bg-[color:var(--gold)] text-black shadow-md font-extrabold'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60'
                }`}
              >
                {pIsB2C ? (
                  <UserCheck size={13} className={isCurrent ? 'text-black' : 'text-emerald-400'} />
                ) : pIsB2B ? (
                  <ShieldCheck size={13} className={isCurrent ? 'text-black' : 'text-cyan-400'} />
                ) : null}
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid of Product Cards with Equal Margins & Symmetrical Borders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {filteredItems.length === 0 ? (
          <div className="col-span-full text-zinc-500 text-sm text-center py-10" data-testid="products-empty">
            No products found matching your search.
          </div>
        ) : (
          filteredItems.map((it, i) => (
            <ProductCard
              key={it.id}
              it={it}
              categoryLabel={active.label}
              isB2C={isB2C}
              isB2B={isB2B}
              delay={i * 50}
              onEnquire={() => onEnquire?.(active, it)}
              onBuy={() => onBuy?.('product', it)}
              buyEnabled={buyEnabled}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ProductCard({ it, categoryLabel, isB2C, isB2B, delay, onEnquire, onBuy, buyEnabled = true }) {
  const Icon = Icons[it.icon] || Icons.Server;
  const [flipped, setFlipped] = useState(false);
  const [copied, setCopied] = useState('');
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoMode, setVideoMode] = useState('desktop');

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    toast.success('Copied!');
    setTimeout(() => setCopied(''), 1200);
  };

  const [isMobileVp, setIsMobileVp] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const onResize = () => setIsMobileVp(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const bgVideoSrc = isMobileVp
    ? (it.videoMobile || it.videoDesktop || it.video)
    : (it.videoDesktop || it.video || it.videoMobile);
  const hasVideo = !!bgVideoSrc;
  const isDirectVideo = hasVideo && /(\.mp4|\.webm|\.mov)$/i.test(bgVideoSrc);
  const hasMobileVid = !!it.videoMobile;
  const hasDesktopVid = !!(it.videoDesktop || it.video);
  const canFullscreen = hasMobileVid || hasDesktopVid;

  return (
    <>
      <div
        id={`product-${it.id}`}
        data-anchor={`product-${it.id}`}
        style={{ perspective: '1200px' }}
        className="relative h-80 transition-all duration-300 scroll-mt-32 hover:scale-[1.01]"
      >
        <div
          className="relative w-full h-full transition-transform duration-700 rounded-2xl"
          style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
        >
          {/* Front */}
          <div
            className={`absolute inset-0 rounded-2xl overflow-hidden border transition-colors flex flex-col justify-between p-4 shadow-lg ${
              isB2B
                ? 'border-cyan-900/60 bg-zinc-950/90 hover:border-cyan-500/60'
                : isB2C
                ? 'border-emerald-900/60 bg-zinc-950/90 hover:border-emerald-500/60'
                : 'border-zinc-800/80 bg-zinc-950/90 hover:border-[color:var(--gold)]/50'
            }`}
            style={{
              backfaceVisibility: 'hidden',
              background: it.gradient || 'linear-gradient(180deg, #181818 0%, #0a0a0a 100%)'
            }}
          >
            {isDirectVideo ? (
              <video key={bgVideoSrc} src={bgVideoSrc} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-70" />
            ) : it.image ? (
              <img key={it.image} src={it.image} alt={it.name} className="absolute inset-0 w-full h-full object-cover opacity-65" />
            ) : null}
            {(isDirectVideo || it.image) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
            )}

            {/* Top Bar: Category Badge + Controls */}
            <div className="relative flex items-center justify-between gap-2 z-10">
              <span className={`px-2.5 py-1 rounded-md text-[11px] font-extrabold tracking-wide border shadow-xs ${
                isB2B
                  ? 'bg-cyan-950/90 border-cyan-700/60 text-cyan-300'
                  : isB2C
                  ? 'bg-emerald-950/90 border-emerald-700/60 text-emerald-300'
                  : 'bg-zinc-900/90 border-zinc-700 text-zinc-300'
              }`}>
                {isB2B ? 'B2B Suite' : isB2C ? 'B2C Platform' : categoryLabel}
              </span>

              <div className="flex items-center gap-1.5">
                {canFullscreen && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoMode(isMobileVp ? (hasMobileVid ? 'mobile' : 'desktop') : (hasDesktopVid ? 'desktop' : 'mobile'));
                      setVideoOpen(true);
                    }}
                    aria-label="View video fullscreen"
                    className="w-7 h-7 rounded-md bg-black/60 hover:bg-black/80 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center"
                  >
                    <Maximize2 size={13} />
                  </button>
                )}
                {it.rating && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/15" data-testid={`product-rating-${it.id}`}>
                    <Star size={11} className="fill-[color:var(--gold)] text-[color:var(--gold)]" />
                    <span className="text-white text-xs font-bold">{it.rating}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Bottom Content: Title + Subtitle + Action Buttons */}
            <div className="relative z-10 mt-auto pt-2">
              <h3 className="text-white font-extrabold text-lg md:text-xl leading-tight drop-shadow truncate">
                {it.name}
              </h3>
              <p className="text-zinc-300 text-xs line-clamp-1 mt-0.5">
                {it.tagline || it.sub || 'Instant deployment & enterprise support'}
              </p>

              {/* Action Buttons Row */}
              <div className="mt-3.5 flex items-center justify-between gap-1.5 pt-2.5 border-t border-white/10">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setFlipped(true)}
                    className="h-8 px-2.5 rounded-lg bg-[color:var(--gold)] hover:brightness-110 text-black text-xs font-extrabold flex items-center gap-1 shadow-sm active:scale-95 transition"
                    title="View Demo Credentials"
                  >
                    <Eye size={13} />
                    <span>Demo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewsOpen(true)}
                    className="h-8 w-8 rounded-lg bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 flex items-center justify-center active:scale-95 transition"
                    title="Reviews"
                  >
                    <MessageSquare size={13} />
                  </button>
                </div>

                <div className="flex items-center gap-1.5 ml-auto">
                  {(Number(it.price || 0) > 0 || (it.pricingKind === 'percent' && Number(it.percent || 0) > 0)) && buyEnabled && (
                    <button
                      type="button"
                      onClick={onBuy}
                      className="h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-extrabold flex items-center gap-1 shadow-sm active:scale-95 transition"
                    >
                      <ShoppingCart size={12} />
                      <span>
                        {it.pricingKind === 'percent' && Number(it.percent || 0) > 0
                          ? `${it.percent}%`
                          : formatMoneyCompact(it.price)}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onEnquire}
                    className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold flex items-center gap-1 border border-zinc-700 active:scale-95 transition"
                    title="Enquire"
                  >
                    <Send size={12} />
                    <span>Enquire</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Back (Demo Credentials) */}
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden border border-[color:var(--gold)]/40 bg-zinc-950 p-4 flex flex-col justify-between shadow-xl"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-[color:var(--gold)]/20 text-[color:var(--gold)] flex items-center justify-center font-bold">
                    <Icon size={14} />
                  </span>
                  <h4 className="text-zinc-100 font-bold text-sm truncate max-w-[180px]">{it.name} — Demo</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setFlipped(false)}
                  className="text-zinc-400 hover:text-white text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800"
                >
                  Back
                </button>
              </div>

              <div className="space-y-2 text-xs">
                <div>
                  <span className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Demo User</span>
                  <div className="mt-1 flex items-center gap-1.5">
                    <input
                      readOnly
                      value={it.demoUser || 'demo_user'}
                      className="w-full h-7 px-2.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => copy(it.demoUser || 'demo_user', 'user')}
                      className="h-7 px-2.5 rounded bg-zinc-800 text-zinc-300 flex items-center gap-1"
                    >
                      {copied === 'user' ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                    </button>
                  </div>
                </div>

                <div>
                  <span className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Password</span>
                  <div className="mt-1 flex items-center gap-1.5">
                    <input
                      readOnly
                      value={it.demoPass || 'demo@123'}
                      className="w-full h-7 px-2.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => copy(it.demoPass || 'demo@123', 'pass')}
                      className="h-7 px-2.5 rounded bg-zinc-800 text-zinc-300 flex items-center gap-1"
                    >
                      {copied === 'pass' ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-zinc-850 flex items-center gap-2">
              {it.demoUrl && (
                <a
                  href={normalizeUrl(it.demoUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 h-8 rounded-lg bg-[color:var(--gold)] text-black font-extrabold text-xs flex items-center justify-center gap-1.5"
                >
                  <ExternalLink size={12} />
                  <span>Launch Demo Site</span>
                </a>
              )}
              <button
                type="button"
                onClick={onEnquire}
                className="h-8 px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs font-bold flex items-center gap-1"
              >
                <Send size={12} />
                <span>Enquire</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {reviewsOpen && (
        <ReviewsModal
          open={reviewsOpen}
          onOpenChange={setReviewsOpen}
          itemType="product"
          itemId={it.id}
          itemName={it.name}
        />
      )}

      {videoOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <div className="relative max-w-4xl w-full bg-zinc-950 rounded-2xl overflow-hidden border border-zinc-800" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
              <span className="text-white font-bold text-sm">{it.name} — Preview</span>
              <button onClick={() => setVideoOpen(false)} className="text-zinc-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="aspect-video bg-black flex items-center justify-center">
              <video src={bgVideoSrc} controls autoPlay className="max-w-full max-h-full" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
